import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { setBreadCrumb, setAppError } from "../../../../store/userSlice";
import SmartDownloadPagination from "../../../../components/SmartDataTable/SmartDownloadPagination";
import ArrivalEmployeeBatch from "./ArrivalEmployeeBatch";
import EditPopup from "./EditPopup";
import { CiEdit } from "react-icons/ci";
import {
  ARRIVAL_ONBOARDING_COLUMNS,
  ARRIVAL_ONBOARDING_CONFIG,
  ARRIVAL_ONBOARDING_SEARCHABLE_FIELDS,
} from "./ArrivalOnboardingConstants";

export let sectorArray = [];

export default function ArrivalOnboarding() {
  const dispatch = useDispatch();
  const token = useMemo(() => localStorage.getItem("nodeToken"), []);
  const [trigger, setTrigger] = useState(0);

  const [openArrivalEmployeeBatch, setOpenArrivalEmployeeBatch] = useState({
    open: false,
    data: [],
    id: "",
    sector: null,
    rowData: null,
  });

  const [editPopup, setEditPopup] = useState({
    open: false,
    flightId: null,
    currentData: null,
  });

  const [currentData, setCurrentData] = useState([]);
  const batchSectionRef = useRef(null);

  useEffect(() => {
    dispatch(setBreadCrumb([{ label: "ARRIVAL ONBOARDING", link: "/dashboard/arrival-onboarding" }]));
  }, []);

  const handleDataLoad = useCallback((data) => {
    setCurrentData(data || []);
    // Extract sector from response — stored in window temporarily
    if (window.__arrivalSector) {
      sectorArray = window.__arrivalSector;
    }
  }, []);

  const handleDownloadComplete = useCallback((result) => {
    dispatch(setAppError({ msg: `Excel Downloaded (${result.recordCount} records)`, color: "success" }));
  }, [dispatch]);

  const handleDownloadError = useCallback((error) => {
    dispatch(setAppError({ msg: error.message || "Download failed" }));
  }, [dispatch]);

  const renderCustomCell = useCallback((item, column, rowIndex, currentPage, pageSize) => {
    if (column.key === 'serial') {
      return (currentPage - 1) * pageSize + rowIndex + 1;
    }

    if (column.key === 'flight_icon') {
      return null; // placeholder for flight icon
    }

    if (column.key === 'sector_name') {
      return item.sector?.name || item.sector_name || "-";
    }

    if (column.key === 'flight_status') {
      const statusColors = {
        'ARRIVED': 'bg-green-100 text-green-700',
        'TRAVELLING': 'bg-blue-100 text-blue-700',
        'PARTIAL': 'bg-yellow-100 text-yellow-700',
        'NOT TRAVELLED': 'bg-red-100 text-red-600',
        'NO CANDIDATES': 'bg-gray-100 text-gray-500',
      };
      const color = statusColors[item.flight_status] || 'bg-gray-100 text-gray-500';
      return (
        <span className={`px-2 py-1 rounded-full text-xs ${color}`}>
          {item.flight_status || "-"}
        </span>
      );
    }

    if (column.key === 'arrival_status') {
      const statusColors = {
        'ARRIVED': 'bg-green-100 text-green-700',
        'TRAVELLING': 'bg-blue-100 text-blue-700',
        'PARTIAL': 'bg-yellow-100 text-yellow-700',
        'NOT TRAVELLED': 'bg-red-100 text-red-600',
        'NO CANDIDATES': 'bg-gray-100 text-gray-500',
      };
      const color = statusColors[item.arrival_status] || 'bg-gray-100 text-gray-500';
      return (
        <span className={`px-2 py-1 rounded-full text-xs ${color}`}>
          {item.arrival_status || "-"}
        </span>
      );
    }

    // if (column.key === 'action') {
    //   return (
    //     <button
    //       className="p-1 px-2 rounded-md ring-1 ring-slate-600"
    //       onClick={(e) => {
    //         e.stopPropagation();
    //         setEditPopup({ open: true, flightId: item.id, currentData: item });
    //       }}
    //     >
    //       <CiEdit size={"1.2rem"} />
    //     </button>
    //   );
    // }

    return null;
  }, []);

  const handleRowClick = useCallback((item) => {
    setOpenArrivalEmployeeBatch({
      open: true,
      data: item?.candidate || [],
      id: item.id,
      sector: item?.sector?.name || item?.sector_name,
      rowData: item,
    });
    setTimeout(() => {
      batchSectionRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 100);
  }, []);

  return (
    <div className="p-2 sm:p-5">
      <SmartDownloadPagination
        apiEndpoint={ARRIVAL_ONBOARDING_CONFIG.API_ENDPOINT}
        token={token}
        columns={ARRIVAL_ONBOARDING_COLUMNS}
        searchableFields={ARRIVAL_ONBOARDING_SEARCHABLE_FIELDS}
        enableHighlighting={true}
        defaultPageSize={ARRIVAL_ONBOARDING_CONFIG.DEFAULT_PAGE_SIZE}
        pageSizeOptions={ARRIVAL_ONBOARDING_CONFIG.PAGE_SIZE_OPTIONS}
        enableDownload={false}
        enableSearch={true}
        searchPlaceholder={ARRIVAL_ONBOARDING_CONFIG.SEARCH_PLACEHOLDER}
        searchDebounceMs={ARRIVAL_ONBOARDING_CONFIG.DEBOUNCE_MS}
        renderCustomCell={renderCustomCell}
        onDataLoad={handleDataLoad}
        onDownloadComplete={handleDownloadComplete}
        onDownloadError={handleDownloadError}
        noDataMessage={ARRIVAL_ONBOARDING_CONFIG.NO_DATA_MESSAGE}
        onRowClick={handleRowClick}
        trigger={trigger}
      />

      {/* Employee Batch Detail */}
      {openArrivalEmployeeBatch.open && (
        <div
          ref={batchSectionRef}
          id="employeeBatch"
          className="mt-4"
        >
          <ArrivalEmployeeBatch
            candidateData={openArrivalEmployeeBatch?.data}
            setUpdatedData={(newData) => {
              setCurrentData(newData);
              setTrigger(prev => prev + 1);
            }}
            batchData={currentData}
            fullBatchData={currentData}
            sector={openArrivalEmployeeBatch?.sector}
            arrivalBatchId={openArrivalEmployeeBatch?.id}
            rowData={openArrivalEmployeeBatch?.rowData}
            setOpenArrivalEmployeeBatch={setOpenArrivalEmployeeBatch}
          />
        </div>
      )}

      {editPopup.open && (
        <EditPopup
          popupState={editPopup}
          setPopupState={setEditPopup}
          setTableData={() => setTrigger(prev => prev + 1)}
        />
      )}
    </div>
  );
}
